// services
import { Http, HttpPromise } from "@shared/svelte/Modules/ApiService";
import { merge } from "lodash";

// stores
import {
  bankAccount,
  BankAccountMetaData,
  updatingBankAccount,
} from "@retailer/stores/BankAccountStore";

// types
import type { HTTPParams } from "@shared/types/HTTP";
import { PlaidCustomization, PlaidModalParams } from "@retailer/types/Plaid";

export const buildPlaidModal = async (params: PlaidModalParams) => {
  const defaultParams: PlaidModalParams = {
    isMicrodeposit: false,
    customization: PlaidCustomization.Default,
    afterSuccess: () => {},
    afterExit: () => {},
    onLoad: () => {},
    afterMicrodepositFailure: () => {},
    plaidAccountIds: null,
  };

  const {
    isMicrodeposit,
    customization,
    afterSuccess,
    afterExit,
    onLoad,
    afterMicrodepositFailure,
    plaidAccountIds,
  } = merge(defaultParams, params);

  const onSuccessWrapper = (public_token: string, metadata: BankAccountMetaData) => {
    onPlaidSuccess(public_token, metadata, afterSuccess);
  };

  return isMicrodeposit
    ? buildMicrodepositToken(
        customization,
        onSuccessWrapper,
        afterExit,
        onLoad,
        afterMicrodepositFailure,
        plaidAccountIds
      )
    : buildLinkToken(customization, onSuccessWrapper, afterExit, onLoad);
};

export const get = async () => {
  const requestData: HTTPParams = {
    method: "GET",
    url: "/api/retailer/payments/plaid_items",
  };

  const errCB = () => {
    updatingBankAccount.set(false);
    console.warn("unable to get user bank data");
    bankAccount.update(state => ({ ...state, loaded: true, failed: true }));
  };

  const successCB = res => {
    updatingBankAccount.set(false);
    bankAccount.set({ plaid_items: res, loaded: true, failed: false });
  };

  updatingBankAccount.set(true);
  await Http(requestData, successCB, errCB);
};

const onPlaidSuccess = async (
  public_token: string,
  metadata: BankAccountMetaData,
  onSuccess: (metadata: BankAccountMetaData) => void
) => {
  const successCB = async () => {
    updatingBankAccount.set(false);
    await get();
    onSuccess(metadata);
  };

  const errCB = () => {
    updatingBankAccount.set(false);
  };

  updatingBankAccount.set(true);
  const requestData: HTTPParams = {
    method: "POST",
    url: "/api/retailer/payments/dwolla_identity",
    data: {
      plaid: {
        token: public_token,
        metadata,
      },
    },
  };

  await Http(requestData, successCB, errCB);
};

const clean = async () => {
  const requestData: HTTPParams = {
    method: "DELETE",
    url: "/api/retailer/payments/plaid_items/clean",
  };

  return await HttpPromise(requestData);
};

const buildLinkToken = async (
  customization: PlaidCustomization,
  onPlaidSuccess: (public_token: string, metadata: BankAccountMetaData) => void,
  onPlaidExit: (error: {}, metadata: BankAccountMetaData) => void,
  onPlaidLoad: () => void
) => {
  const linkToken = await requestLinkToken(customization);
  if (!linkToken) {
    get();
    return;
  }

  // @ts-ignore
  const plaid = Plaid.create({
    token: linkToken,
    env: getEnv(),
    onSuccess: onPlaidSuccess,
    onExit: onPlaidExit,
    onLoad: onPlaidLoad,
  });

  return plaid;
};

const buildMicrodepositToken = async (
  customization: PlaidCustomization,
  onPlaidSuccess: (public_token: string, metadata: BankAccountMetaData) => void,
  onPlaidExit: (error: {}, metadata: BankAccountMetaData) => void,
  onPlaidLoad: () => void,
  afterMicrodepositFailure: () => void,
  plaidAccountIds: number[] = null
) => {
  const linkTokens = await requestMicrodepositLinkTokens(customization, plaidAccountIds);
  if (!linkTokens) {
    get();
    return;
  }

  let plaidModals = {};
  linkTokens.forEach(token => {
    const plaidAccountId = token["account_id"];

    const onPlaidExitWrapper = async (error: {}, metadata: any) => {
      const cleanedAccounts = await clean();

      let accountCleaned = cleanedAccounts.some(account => {
        return account.account_id == plaidAccountId;
      });

      if (accountCleaned) {
        get();
        afterMicrodepositFailure();
      }

      onPlaidExit(error, metadata);
    };

    // @ts-ignore
    let plaid = Plaid.create({
      token: token["link_token"],
      env: getEnv(),
      onSuccess: onPlaidSuccess,
      onExit: onPlaidExitWrapper,
      onLoad: onPlaidLoad,
    });
    if (plaidAccountId !== null) {
      plaidModals[plaidAccountId] = plaid;
    } else {
      plaidModals = plaid;
    }
  });

  return plaidModals;
};

const requestLinkToken = async (customization: PlaidCustomization) => {
  // Not supplying a customization value will return the default customization
  const data = customization == PlaidCustomization.Default ? {} : { customization };
  const requestData: HTTPParams = {
    method: "POST",
    url: "/api/retailer/plaid/link_tokens",
    data,
  };

  const successCB = json => json;

  const errCB = () => {};
  const res = await Http(requestData, successCB, errCB);
  return res["link_token"];
};

const requestMicrodepositLinkTokens = async (
  customization: PlaidCustomization,
  plaidAccountIds: number[] = null
) => {
  // Not supplying a customization value will return the default customization
  const data = {};
  if (plaidAccountIds !== null) {
    data["plaid_account_ids"] = plaidAccountIds;
  }
  if (customization != PlaidCustomization.Default) {
    data[customization] = customization;
  }

  const requestData: HTTPParams = {
    method: "GET",
    url: "/api/retailer/plaid/link_tokens",
    data,
  };

  const successCB = json => json;

  const errCB = () => {};
  const res = await Http(requestData, successCB, errCB);
  return res["link_tokens"];
};

const getEnv = () => (window["C"].env === "production" ? "production" : "sandbox");
